import React from 'react';
import './UserTable.scss';

const UserTable = ({ 
  users, 
  loading, 
  pagination, 
  onUserSelect, 
  onUserEdit, 
  searchTerm,
  onSearch, // This is now the setSearchTerm function from the parent
  onPageChange,
  selectedUser,
  sortBy,
  sortOrder,
  onSort
}) => {
  const USERS_PER_PAGE = pagination.limit || 20;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages && !loading) {
      onPageChange(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatRole = (role) => {
    if (!role) return 'N/A';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Users are now sorted by the backend, no need for client-side sorting
  const sortedUsers = users;

  const renderSkeletonRows = () => {
    return Array.from({ length: USERS_PER_PAGE }, (_, index) => (
      <tr key={`skeleton-${index}`} className="user-table__row user-table__row--skeleton">
        <td className="user-table__cell"><div className="user-table__skeleton user-table__skeleton--username"></div></td>
        <td className="user-table__cell"><div className="user-table__skeleton user-table__skeleton--role"></div></td>
        <td className="user-table__cell"><div className="user-table__skeleton"></div></td>
        <td className="user-table__cell"><div className="user-table__skeleton"></div></td>
        <td className="user-table__cell user-table__cell--centered"><div className="user-table__skeleton user-table__skeleton--number"></div></td>
        <td className="user-table__cell user-table__cell--centered"><div className="user-table__skeleton user-table__skeleton--number"></div></td>
        <td className="user-table__cell user-table__cell--centered"><div className="user-table__skeleton user-table__skeleton--number"></div></td>
        <td className="user-table__cell user-table__cell--actions"><div className="user-table__skeleton user-table__skeleton--button"></div></td>
      </tr>
    ));
  };
  
  const renderTableRows = () => {
    if (loading) {
      return renderSkeletonRows();
    }

    if (sortedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="user-table__empty-cell">
            <div className="user-table__empty">No users found.</div>
          </td>
        </tr>
      );
    }

    return sortedUsers.map((user) => (
      <tr 
        key={user.id}
        className={`user-table__row ${selectedUser?.id === user.id ? 'user-table__row--selected' : ''}`}
        onClick={() => onUserSelect(user.id === selectedUser?.id ? null : user)}
      >
        <td className="user-table__cell user-table__cell--username">{user.username}</td>
        <td className="user-table__cell"><span className={`user-table__role user-table__role--${user.role}`}>{formatRole(user.role)}</span></td>
        <td className="user-table__cell">{formatDate(user.created_at)}</td>
        <td className="user-table__cell">{formatDate(user.updated_at)}</td>
        <td className="user-table__cell user-table__cell--centered">{user.total_games_across_all || 0}</td>
        <td className="user-table__cell user-table__cell--centered">{user.max_current_streak || 0}</td>
        <td className="user-table__cell user-table__cell--centered">{user.max_best_streak || 0}</td>
        <td className="user-table__cell user-table__cell--actions">
          <button onClick={(e) => { e.stopPropagation(); onUserEdit(user); }} className="user-table__edit-button">Edit</button>
        </td>
      </tr>
    ));
  };

  const renderPaginationControls = () => {
    const { currentPage, totalPages } = pagination;
    return (
      <div className="user-table__pagination-controls">
        <button className="user-table__pagination-button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}>← Prev</button>
        <button className="user-table__pagination-button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading}>Next →</button>
      </div>
    );
  };

  return (
    <div className="user-table">
      <div className="user-table__controls">
        <div className="user-table__search">
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            className="user-table__search-input"
          />
        </div>
        <div className="user-table__info">
          Showing {users.length} of {pagination.totalUsers} users
        </div>
      </div>

      <div className="user-table__container">
        <table className="user-table__table">
          <thead>
            <tr>
              <th onClick={() => onSort('username')} className="user-table__header user-table__header--sortable">
                <span className="user-table__header-content">Username<span className={`user-table__sort-icon ${sortBy === 'username' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'username' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th onClick={() => onSort('role')} className="user-table__header user-table__header--sortable">
                <span className="user-table__header-content">Role<span className={`user-table__sort-icon ${sortBy === 'role' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'role' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th onClick={() => onSort('created_at')} className="user-table__header user-table__header--sortable">
                <span className="user-table__header-content">Created<span className={`user-table__sort-icon ${sortBy === 'created_at' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th onClick={() => onSort('updated_at')} className="user-table__header user-table__header--sortable">
                <span className="user-table__header-content">Last Activity<span className={`user-table__sort-icon ${sortBy === 'updated_at' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'updated_at' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th onClick={() => onSort('total_games_across_all')} className="user-table__header user-table__header--sortable user-table__header--centered">
                <span className="user-table__header-content">Total Games<span className={`user-table__sort-icon ${sortBy === 'total_games_across_all' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'total_games_across_all' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th className="user-table__header user-table__header--centered">
                <span className="user-table__header-content">Current Streak</span>
              </th>
              <th onClick={() => onSort('max_best_streak')} className="user-table__header user-table__header--sortable user-table__header--centered">
                <span className="user-table__header-content">Best Streak<span className={`user-table__sort-icon ${sortBy === 'max_best_streak' ? 'user-table__sort-icon--visible' : ''}`}>{sortBy === 'max_best_streak' && (sortOrder === 'asc' ? '↑' : '↓')}</span></span>
              </th>
              <th className="user-table__header user-table__header--centered">Actions</th>
            </tr>
          </thead>
          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>

      {(pagination.totalPages > 1) && (
        <div className="user-table__pagination">
          <div className="user-table__pagination-info">Page {pagination.currentPage} of {pagination.totalPages}</div>
          {renderPaginationControls()}
        </div>
      )}
    </div>
  );
};

export default UserTable;